import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownProps, Grid, Icon, Segment, Table } from "semantic-ui-react";
import { Link } from "react-router-dom";
import Datepicker from "react-datepicker";
import agent from "../../../app/api/agent";
import { downloadFile } from "../../../app/helpers/downloadFile";
import { prettifyDate, prettifyDateWithoutTime } from "../../../app/helpers/dateHelpers";
import Loader from "../../../components/Loader";
import PageTitle from "../../../components/page-title/PageTitle";
import {
  APPLICATION_STATUS_LABELS,
  ApplicationStatus,
  CompanyParticipant,
  PARTICIPANT_STATUS_OPTIONS,
} from "../models";
import useToaster from "../../../app/hooks/useToaster";

const SemanticDateInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <div className="ui input">
      <input ref={ref} {...props} />
    </div>
  )
);

const TYPE_OPTIONS = [
  { key: "all", value: "all", text: "Visi" },
  { key: "shadowing", value: "shadowing", text: "Šešėliavimas" },
  { key: "internship", value: "internship", text: "Praktika" },
];

const DEFAULT_STATUSES = [ApplicationStatus.CompanyApproved];

const CompanyReportsParticipants = () => {
  const { t } = useTranslation();
  const { apiError } = useToaster();

  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [statuses, setStatuses] = useState<ApplicationStatus[]>(DEFAULT_STATUSES);
  const [typeFilter, setTypeFilter] = useState<"all" | "shadowing" | "internship">("all");
  const [participants, setParticipants] = useState<CompanyParticipant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const buildFilter = useCallback(() => {
    const filter: { from?: string; to?: string; status?: number[]; isInternship?: boolean } = {};
    if (from) filter.from = from.toISOString();
    if (to) filter.to = to.toISOString();
    if (statuses.length > 0) filter.status = statuses;
    if (typeFilter === "shadowing") filter.isInternship = false;
    if (typeFilter === "internship") filter.isInternship = true;
    return filter;
  }, [from, to, statuses, typeFilter]);

  useEffect(() => {
    setIsLoading(true);
    agent.Reports.companyParticipants(buildFilter())
      .then(setParticipants)
      .catch(apiError)
      .finally(() => setIsLoading(false));
  }, [from, to, statuses, typeFilter]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await agent.Reports.companyParticipantsExport(buildFilter());
      downloadFile(response.data, `dalyviai-${prettifyDate(new Date().toString())}.xlsx`);
    } catch (e) {
      apiError(e);
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (p: CompanyParticipant) => {
    if (p.isInternship) {
      const start = prettifyDateWithoutTime(p.date);
      return p.endDate ? `${start} – ${prettifyDateWithoutTime(p.endDate)}` : start;
    }
    return prettifyDate(p.date);
  };

  const showStatusColumn = statuses.length !== 1 || statuses[0] !== ApplicationStatus.CompanyApproved;

  const handleStatusChange = (_: React.SyntheticEvent, data: DropdownProps) => {
    const selected = data.value as number[];
    setStatuses(selected.length > 0 ? selected : DEFAULT_STATUSES);
  };

  return (
    <>
      <PageTitle title="Dalyvių informacija" />
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={3}>
            <div className="ui form">
              <div className="field">
                <label>{t("common.date_from")}</label>
                <Datepicker
                  selected={from}
                  onChange={(date) => setFrom(date)}
                  selectsStart
                  startDate={from}
                  endDate={to}
                  dateFormat="yyyy-MM-dd"
                  locale="lt"
                  placeholderText={t("common.date_from")}
                  isClearable
                  customInput={<SemanticDateInput />}
                />
              </div>
            </div>
          </Grid.Column>
          <Grid.Column width={3}>
            <div className="ui form">
              <div className="field">
                <label>{t("common.date_to")}</label>
                <Datepicker
                  selected={to}
                  onChange={(date) => setTo(date)}
                  selectsEnd
                  startDate={from}
                  endDate={to}
                  minDate={from ?? undefined}
                  dateFormat="yyyy-MM-dd"
                  locale="lt"
                  placeholderText={t("common.date_to")}
                  isClearable
                  customInput={<SemanticDateInput />}
                />
              </div>
            </div>
          </Grid.Column>
          <Grid.Column width={5}>
            <div className="ui form">
              <div className="field">
                <label>Statusas</label>
                <Dropdown
                  placeholder="Pasirinkite statusą"
                  fluid
                  multiple
                  selection
                  options={PARTICIPANT_STATUS_OPTIONS.map((o) => ({
                    key: o.value,
                    value: o.value,
                    text: o.text,
                  }))}
                  value={statuses}
                  onChange={handleStatusChange}
                />
              </div>
            </div>
          </Grid.Column>
          <Grid.Column width={3}>
            <div className="ui form">
              <div className="field">
                <label>Tipas</label>
                <Dropdown
                  fluid
                  selection
                  options={TYPE_OPTIONS}
                  value={typeFilter}
                  onChange={(_, data) => setTypeFilter(data.value as "all" | "shadowing" | "internship")}
                />
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            handleExport();
          }}
          style={{ cursor: isExporting ? "wait" : "pointer", pointerEvents: isExporting ? "none" : "auto" }}
        >
          <Icon name="file excel outline" style={{ marginRight: "0.5rem" }} />
          {isExporting ? t("common.loading") : t("common.exportToExcel")}
        </Link>
      </div>
      {isLoading ? (
        <Loader />
      ) : participants.length === 0 ? (
        <>{t("common.no_records")}</>
      ) : (
        <Segment>
          <Table basic="very" unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("common.student")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.email")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.phone")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.grade")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.school")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.position")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.date")}</Table.HeaderCell>
                <Table.HeaderCell>{t("common.no_show")}</Table.HeaderCell>
                {showStatusColumn && <Table.HeaderCell>Statusas</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {participants.map((p, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{p.fullName}</Table.Cell>
                  <Table.Cell>
                    <a href={`mailto:${p.email}`}>{p.email}</a>
                  </Table.Cell>
                  <Table.Cell>{p.phoneNumber}</Table.Cell>
                  <Table.Cell>{p.grade}</Table.Cell>
                  <Table.Cell>{p.school}</Table.Cell>
                  <Table.Cell>{p.position}</Table.Cell>
                  <Table.Cell>{formatDate(p)}</Table.Cell>
                  <Table.Cell>{p.noShow ? t("common.no_show") : ""}</Table.Cell>
                  {showStatusColumn && <Table.Cell>{APPLICATION_STATUS_LABELS[p.status] ?? p.status}</Table.Cell>}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
    </>
  );
};

export default CompanyReportsParticipants;
